import { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import { Feature, FeatureCollection, Point, Polygon } from 'geojson';
import 'mapbox-gl/dist/mapbox-gl.css';
import { cn } from '@/trailmix/lib/cn';
import colors from 'tailwindcss/colors';
import { router } from '@inertiajs/react';
import { getImageFromCDN } from '@/image-cdn';

const MAPBOX_MAP_ID = 'mapbox_map';
const PLACES_SOURCE_ID = 'places';
const MARKER_LAYER_ID = 'places-markers';
const LABEL_LAYER_ID = 'places-labels';
const CLUSTER_LAYER_ID = 'places-clusters';
const CLUSTER_COUNT_LAYER_ID = 'places-cluster-count';
const PHOTO_MARKER_LAYER_ID = 'places-photo-markers';
const RADIUS_CIRCLE_SOURCE_ID = 'radius-circle';
const RADIUS_CIRCLE_LAYER_ID = 'radius-circle-layer';
const USER_LOCATION_SOURCE_ID = 'user-location';
const USER_LOCATION_LAYER_ID = 'user-location-layer';
const DEFAULT_ZOOM = 3;
const MIN_ZOOM = 1.25;
const MAX_ZOOM = 14;
const DEFAULT_CENTER: mapboxgl.LngLatLike = [-42.7106537, 32.1457768]; // United States
const DEFAULT_PADDING: mapboxgl.PaddingOptions = { top: 50, bottom: 50, right: 50, left: 50 };
const PHOTO_IMAGE_PIXEL_SIZE = 88; // rendered at 2x → 44px logical

const VISITED_MARKER_FILL = colors.green[600];
const VISITED_MARKER_STROKE = colors.green[900];
const PLANNING_MARKER_FILL = colors.gray[400];
const PLANNING_MARKER_STROKE = colors.gray[600];
const MARKER_LABEL = colors.gray[800];

// Helper function to create a circle polygon from center point and radius
function createCircleFeature(center: Point, radiusInMiles: number): Feature<Polygon> {
    const radiusInKm = radiusInMiles * 1.60934;
    const points = 64;
    const coords = {
        longitude: center.coordinates[0],
        latitude: center.coordinates[1],
    };

    const km = radiusInKm;
    const ret: [number, number][] = [];
    const distanceX = km / (111.32 * Math.cos((coords.latitude * Math.PI) / 180));
    const distanceY = km / 110.574;

    for (let i = 0; i < points; i++) {
        const theta = (i / points) * (2 * Math.PI);
        const x = distanceX * Math.cos(theta);
        const y = distanceY * Math.sin(theta);
        ret.push([coords.longitude + x, coords.latitude + y]);
    }
    ret.push(ret[0]); // Close the polygon

    return {
        type: 'Feature' as const,
        geometry: {
            type: 'Polygon' as const,
            coordinates: [ret],
        },
        properties: {},
    };
}

const loadingImageIds = new Set<string>();
const loadedImageIds = new Set<string>();

// Adds a fully-transparent placeholder so Mapbox doesn't warn about a missing image
// while the real photo is still loading over the network.
function addTransparentPlaceholder(map: mapboxgl.Map, imageId: string): void {
    if (map.hasImage(imageId)) return;
    const size = PHOTO_IMAGE_PIXEL_SIZE;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    map.addImage(imageId, canvas.getContext('2d')!.getImageData(0, 0, size, size), { pixelRatio: 2 });
}

// Draws a photo as a circular canvas image and registers it with the map.
// Replaces any transparent placeholder already registered for this id.
// No-ops if the real image is already loaded or a load is in flight. Fails silently on load error.
async function loadPhotoMarkerImage(map: mapboxgl.Map, imageId: string, photoUrl: string): Promise<void> {
    if (loadedImageIds.has(imageId)) return;
    if (loadingImageIds.has(imageId)) return;
    loadingImageIds.add(imageId);

    return new Promise(resolve => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            const size = PHOTO_IMAGE_PIXEL_SIZE;
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d')!;
            const r = size / 2;
            const borderW = 5; // 2.5px at pixelRatio 2

            // Clip to circle and draw photo
            ctx.save();
            ctx.beginPath();
            ctx.arc(r, r, r - borderW, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, 0, 0, size, size);
            ctx.restore();

            // Photo border ring
            ctx.beginPath();
            ctx.arc(r, r, r - borderW / 2, 0, Math.PI * 2);
            ctx.strokeStyle = '#fff';
            ctx.lineWidth = borderW;
            ctx.stroke();

            // White ring behind the green dot
            const dotR = 13; // 6.5px at pixelRatio 2
            const dotX = size - dotR - 5;
            const dotY = dotR + 5;
            ctx.beginPath();
            ctx.arc(dotX, dotY, dotR + 6, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();

            // Green visited dot
            ctx.beginPath();
            ctx.arc(dotX, dotY, dotR, 0, Math.PI * 2);
            ctx.fillStyle = VISITED_MARKER_FILL;
            ctx.fill();
            ctx.strokeStyle = VISITED_MARKER_STROKE;
            ctx.lineWidth = 3;
            ctx.stroke();

            if (map.hasImage(imageId)) map.removeImage(imageId);
            map.addImage(imageId, ctx.getImageData(0, 0, size, size), { pixelRatio: 2 });
            loadingImageIds.delete(imageId);
            loadedImageIds.add(imageId);
            resolve();
        };
        img.onerror = () => {
            loadingImageIds.delete(imageId);
            resolve();
        };
        img.src = photoUrl;
    });
}

type MapboxMapV2Props = {
    collection: FeatureCollection<Point>;
    className?: string;
    cooperativeGestures?: boolean;
    centerLocation?: Point | null;
    radiusInMiles?: number | null;
    showPhotoMarkers?: boolean;
};

// TODO: each instance should generate a unique ID for the map container (allow multiple maps on one page)
export function MapboxMapV2({
    collection,
    className,
    cooperativeGestures = true,
    centerLocation = null,
    radiusInMiles = null,
    showPhotoMarkers = false,
}: MapboxMapV2Props) {
    const map = useRef<mapboxgl.Map | null>(null);
    const [mapLoadDone, setMapLoadDone] = useState(false);
    const [mapReady, setMapReady] = useState(false);
    const [overlayGone, setOverlayGone] = useState(false);
    const [webglSupported] = useState(() => mapboxgl.supported());
    const isFirstLoad = useRef(true);
    const collectionRef = useRef<FeatureCollection<Point>>(collection);
    collectionRef.current = collection;
    const showPhotoMarkersRef = useRef(showPhotoMarkers);
    showPhotoMarkersRef.current = showPhotoMarkers;

    // Instantiate the map on page load
    useEffect(() => {
        if (map.current) return;
        if (!webglSupported) return;

        map.current = new mapboxgl.Map({
            container: MAPBOX_MAP_ID,
            center: DEFAULT_CENTER,
            zoom: DEFAULT_ZOOM - 1,
            minZoom: MIN_ZOOM,
            cooperativeGestures,
            renderWorldCopies: false,
        })
            .addControl(new mapboxgl.FullscreenControl())
            .once('load', () => {
                // Initialize an empty GeoJSON source, we'll add the place data later
                map.current?.addSource(PLACES_SOURCE_ID, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [],
                    },
                    cluster: true,
                    clusterMaxZoom: 8,
                    clusterRadius: 50,
                });

                // Initialize radius circle source
                map.current?.addSource(RADIUS_CIRCLE_SOURCE_ID, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [],
                    },
                });

                // Initialize user location source
                map.current?.addSource(USER_LOCATION_SOURCE_ID, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [],
                    },
                });

                // Add radius circle layer (should be below markers)
                map.current?.addLayer({
                    id: RADIUS_CIRCLE_LAYER_ID,
                    type: 'fill',
                    source: RADIUS_CIRCLE_SOURCE_ID,
                    paint: {
                        'fill-color': colors.blue[500],
                        'fill-opacity': 0.1,
                    },
                });

                // Add border for the circle
                map.current?.addLayer({
                    id: RADIUS_CIRCLE_LAYER_ID + '-border',
                    type: 'line',
                    source: RADIUS_CIRCLE_SOURCE_ID,
                    paint: {
                        'line-color': colors.blue[500],
                        'line-width': 2,
                        'line-opacity': 0.5,
                    },
                });

                // Add user location marker layers if centerLocation is provided
                // Outer pulsing circle
                map.current?.addLayer({
                    id: USER_LOCATION_LAYER_ID + '-outer',
                    type: 'circle',
                    source: USER_LOCATION_SOURCE_ID,
                    paint: {
                        'circle-color': colors.blue[500],
                        'circle-radius': 12,
                        'circle-opacity': 0.3,
                    },
                });

                // Inner solid circle
                map.current?.addLayer({
                    id: USER_LOCATION_LAYER_ID,
                    type: 'circle',
                    source: USER_LOCATION_SOURCE_ID,
                    paint: {
                        'circle-color': colors.blue[500],
                        'circle-radius': 6,
                        'circle-stroke-width': 2,
                        'circle-stroke-color': colors.white,
                    },
                });

                // Add cluster circle layer
                map.current?.addLayer({
                    id: CLUSTER_LAYER_ID,
                    type: 'circle',
                    source: PLACES_SOURCE_ID,
                    filter: ['has', 'point_count'],
                    paint: {
                        'circle-color': colors.orange[300],
                        'circle-opacity': 0.8,
                        'circle-radius': ['step', ['get', 'point_count'], 18, 10, 24, 50, 30],
                        'circle-stroke-width': 2,
                        'circle-stroke-color': colors.orange[400],
                        'circle-stroke-opacity': 0.7,
                    },
                });

                // Add cluster count label layer
                map.current?.addLayer({
                    id: CLUSTER_COUNT_LAYER_ID,
                    type: 'symbol',
                    source: PLACES_SOURCE_ID,
                    filter: ['has', 'point_count'],
                    layout: {
                        'text-field': '{point_count_abbreviated}',
                        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
                        'text-size': 13,
                    },
                    paint: {
                        'text-color': colors.orange[900],
                    },
                });

                // Add layer for map markers (photo markers get their own layer when the flag is on)
                map.current?.addLayer({
                    id: MARKER_LAYER_ID,
                    type: 'circle',
                    source: PLACES_SOURCE_ID,
                    filter: showPhotoMarkersRef.current
                        ? ['all', ['!', ['has', 'point_count']], ['!', ['has', 'photo_path']]]
                        : ['!', ['has', 'point_count']],
                    paint: {
                        'circle-color': [
                            'case',
                            ['to-boolean', ['get', 'visited']],
                            VISITED_MARKER_FILL,
                            PLANNING_MARKER_FILL,
                        ],
                        'circle-radius': [
                            'interpolate',
                            ['linear'],
                            ['zoom'],
                            0,
                            4, // At zoom 0 (world view), radius is 4
                            4,
                            4, // At zoom 4, radius is 5
                            8,
                            5, // At zoom 8, radius is 5
                            12,
                            7, // At zoom 12, radius is 6
                        ],
                        'circle-stroke-width': 2,
                        'circle-stroke-color': [
                            'case',
                            ['to-boolean', ['get', 'visited']],
                            VISITED_MARKER_STROKE,
                            PLANNING_MARKER_STROKE,
                        ],
                    },
                });

                if (showPhotoMarkersRef.current) {
                    // Add symbol layer for photo markers — rendered in WebGL so they zoom/pan without drifting
                    map.current?.addLayer({
                        id: PHOTO_MARKER_LAYER_ID,
                        type: 'symbol',
                        source: PLACES_SOURCE_ID,
                        filter: ['all', ['!', ['has', 'point_count']], ['has', 'photo_path']],
                        layout: {
                            'icon-image': ['concat', 'photo_marker_', ['to-string', ['id']]],
                            'icon-size': 1,
                            'icon-allow-overlap': true,
                            'icon-ignore-placement': true,
                        },
                    });
                }

                // Add layer for map marker labels
                map.current?.addLayer({
                    id: LABEL_LAYER_ID,
                    type: 'symbol',
                    source: PLACES_SOURCE_ID,
                    filter: ['!', ['has', 'point_count']],
                    layout: {
                        'text-field': ['get', 'name'],
                        'text-variable-anchor': ['left', 'right'],
                        'text-offset': showPhotoMarkersRef.current
                            ? ['case', ['has', 'photo_path'], ['literal', [1.85, 0]], ['literal', [0.75, 0]]]
                            : ['literal', [0.75, 0]],
                        'text-justify': 'auto',
                        'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
                        'text-size': 14,
                        'text-max-width': 15,
                    },
                    paint: {
                        'text-color': MARKER_LABEL,
                        'text-halo-color': colors.white,
                        'text-halo-width': 1,
                    },
                });

                // Zoom into cluster on click
                map.current?.on('click', CLUSTER_LAYER_ID, e => {
                    const features = map.current?.queryRenderedFeatures(e.point, { layers: [CLUSTER_LAYER_ID] });
                    if (!features?.length) return;

                    const clusterId = features[0].properties?.cluster_id;
                    const source = map.current?.getSource(PLACES_SOURCE_ID) as mapboxgl.GeoJSONSource;

                    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
                        if (err || zoom == null) return;
                        map.current?.easeTo({
                            center: (features[0].geometry as Point).coordinates as [number, number],
                            zoom: Math.min(zoom + 1.5, MAX_ZOOM),
                        });
                    });
                });

                // Pointer cursor on cluster hover
                map.current?.on('mouseenter', CLUSTER_LAYER_ID, () => {
                    if (map.current) map.current.getCanvas().style.cursor = 'pointer';
                });
                map.current?.on('mouseleave', CLUSTER_LAYER_ID, () => {
                    if (map.current) map.current.getCanvas().style.cursor = '';
                });

                // Navigate to place page on marker click
                map.current?.on('click', MARKER_LAYER_ID, async e => {
                    const features = map.current?.queryRenderedFeatures(e.point, { layers: [MARKER_LAYER_ID] });
                    if (!features?.length) return;

                    const placeId = features[0].id;
                    if (!placeId) return;

                    if (route().current('places.show', { place: placeId })) return;

                    if (document.fullscreenElement) {
                        await document.exitFullscreen();
                    }

                    router.visit(route('places.show', { place: placeId }));
                });

                // Pointer cursor on marker hover
                map.current?.on('mouseenter', MARKER_LAYER_ID, () => {
                    if (map.current) map.current.getCanvas().style.cursor = 'pointer';
                });
                map.current?.on('mouseleave', MARKER_LAYER_ID, () => {
                    if (map.current) map.current.getCanvas().style.cursor = '';
                });

                if (showPhotoMarkersRef.current) {
                    // Navigate to place page on photo marker click
                    map.current?.on('click', PHOTO_MARKER_LAYER_ID, async e => {
                        const features = map.current?.queryRenderedFeatures(e.point, { layers: [PHOTO_MARKER_LAYER_ID] });
                        if (!features?.length) return;

                        const placeId = features[0].id;
                        if (!placeId) return;

                        if (route().current('places.show', { place: placeId })) return;

                        if (document.fullscreenElement) {
                            await document.exitFullscreen();
                        }

                        router.visit(route('places.show', { place: placeId }));
                    });

                    // Pointer cursor on photo marker hover
                    map.current?.on('mouseenter', PHOTO_MARKER_LAYER_ID, () => {
                        if (map.current) map.current.getCanvas().style.cursor = 'pointer';
                    });
                    map.current?.on('mouseleave', PHOTO_MARKER_LAYER_ID, () => {
                        if (map.current) map.current.getCanvas().style.cursor = '';
                    });

                    // Suppress the "could not be loaded" warning by registering a transparent placeholder
                    // synchronously, then replace it with the real photo once it loads from the network.
                    map.current?.on('styleimagemissing', (e: { id: string }) => {
                        const { id } = e;
                        if (!id.startsWith('photo_marker_') || !map.current) return;
                        addTransparentPlaceholder(map.current, id);
                        const featureId = Number(id.replace('photo_marker_', ''));
                        const feature = collectionRef.current.features.find(f => f.id === featureId);
                        if (!feature?.properties?.photo_path) return;
                        const photoUrl = getImageFromCDN({
                            path: feature.properties.photo_path as string,
                            size: 'small',
                        }).toURL();
                        void loadPhotoMarkerImage(map.current, id, photoUrl);
                    });
                }

                setMapLoadDone(true);
            });

        return () => {
            // map.current?.remove();
        };
    }, [cooperativeGestures]);

    // Update the map whenever the FeatureCollection changes
    useEffect(() => {
        if (!map.current) return;
        if (!mapLoadDone) return;

        // Update custom GeoJSON source with data about each place
        (map.current.getSource(PLACES_SOURCE_ID) as mapboxgl.GeoJSONSource).setData(collection);

        if (showPhotoMarkers) {
            // Load photo marker images for the WebGL symbol layer
            for (const feature of collection.features) {
                if (!feature.properties?.photo_path || feature.id == null) continue;
                const imageId = `photo_marker_${feature.id}`;
                const photoUrl = getImageFromCDN({ path: feature.properties.photo_path as string, size: 'tiny' }).toURL();
                void loadPhotoMarkerImage(map.current, imageId, photoUrl);
            }
        }

        // Update user location marker if centerLocation is provided
        if (centerLocation) {
            const locationFeature: Feature<Point> = {
                type: 'Feature',
                geometry: centerLocation,
                properties: {},
            };
            (map.current.getSource(USER_LOCATION_SOURCE_ID) as mapboxgl.GeoJSONSource).setData({
                type: 'FeatureCollection',
                features: [locationFeature],
            });
        } else {
            // Clear the user location if no centerLocation
            (map.current.getSource(USER_LOCATION_SOURCE_ID) as mapboxgl.GeoJSONSource).setData({
                type: 'FeatureCollection',
                features: [],
            });
        }

        // Update radius circle if center and radius are provided
        if (centerLocation && radiusInMiles) {
            const circleFeature = createCircleFeature(centerLocation, radiusInMiles);
            (map.current.getSource(RADIUS_CIRCLE_SOURCE_ID) as mapboxgl.GeoJSONSource).setData({
                type: 'FeatureCollection',
                features: [circleFeature],
            });
        } else {
            // Clear the radius circle if no center/radius
            (map.current.getSource(RADIUS_CIRCLE_SOURCE_ID) as mapboxgl.GeoJSONSource).setData({
                type: 'FeatureCollection',
                features: [],
            });
        }

        if (centerLocation && radiusInMiles) {
            // If we have a center and radius, animate fit to the circle bounds
            const radiusInKm = radiusInMiles * 1.60934;
            const distanceX = radiusInKm / (111.32 * Math.cos((centerLocation.coordinates[1] * Math.PI) / 180));
            const distanceY = radiusInKm / 110.574;

            const bounds = new mapboxgl.LngLatBounds();
            bounds.extend([centerLocation.coordinates[0] - distanceX, centerLocation.coordinates[1] - distanceY]);
            bounds.extend([centerLocation.coordinates[0] + distanceX, centerLocation.coordinates[1] + distanceY]);

            map.current.fitBounds(bounds, {
                padding: DEFAULT_PADDING,
                animate: !isFirstLoad.current,
            });
        } else if (!collection.features.length) {
            // Animate to default map position if it's empty
            map.current.easeTo({
                zoom: DEFAULT_ZOOM - 1.5,
                center: DEFAULT_CENTER,
                padding: DEFAULT_PADDING,
                animate: !isFirstLoad.current,
            });
        } else {
            // Calculate bounding box of all coordinate points
            const bounds = new mapboxgl.LngLatBounds();
            collection.features.forEach(feature => {
                if (feature.bbox) {
                    // Add points to bound larger features, e.g. states
                    const bbox = feature.bbox;
                    bounds.extend([bbox[0], bbox[1]]);
                    bounds.extend([bbox[2], bbox[3]]);
                } else {
                    bounds.extend([feature.geometry.coordinates[0], feature.geometry.coordinates[1]]);
                }
            });

            // Animate the map to the new bounding box on subsequent loads
            map.current.fitBounds(bounds, {
                maxZoom: MAX_ZOOM,
                padding: DEFAULT_PADDING,
                animate: !isFirstLoad.current,
            });
        }

        isFirstLoad.current = false;
        setMapReady(true);

        // TODO: spin the globe if pins are really spread out?
    }, [collection, mapLoadDone, centerLocation, radiusInMiles]);

    // Resize the map when the window resizes
    useEffect(() => {
        function resizeMap() {
            map.current?.resize();
        }

        window.addEventListener('resize', resizeMap);

        return () => {
            window.removeEventListener('resize', resizeMap);
        };
    }, []);

    // Update cooperativeGestures if it changes
    useEffect(() => {
        if (!map.current) return;

        map.current.setCooperativeGestures(cooperativeGestures);
    }, [cooperativeGestures]);

    if (!webglSupported) {
        return (
            <div className={cn('relative flex h-full w-full items-center justify-center bg-gray-100', className)}>
                <p className='text-sm text-gray-500'>Map unavailable — your browser does not support WebGL.</p>
            </div>
        );
    }

    return (
        <div className={cn('relative h-full w-full', className)}>
            {!overlayGone && (
                <div
                    className='absolute inset-0 z-10 bg-gray-100 transition-opacity duration-300'
                    style={{
                        backgroundImage:
                            'linear-gradient(to right, #d1d5db 1px, transparent 1px), linear-gradient(to bottom, #d1d5db 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                        opacity: mapReady ? 0 : 1,
                    }}
                    onTransitionEnd={() => setOverlayGone(true)}
                />
            )}
            <div id={MAPBOX_MAP_ID} className='h-full w-full' />
        </div>
    );
}

// https://docs.mapbox.com/mapbox-gl-js/guides/
// https://docs.mapbox.com/mapbox-gl-js/example/popup-on-hover/
// https://codepen.io/rsh412/pen/PowGGpJ
// https://docs.mapbox.com/mapbox-gl-js/example/change-case-of-labels/
// https://docs.mapbox.com/mapbox-gl-js/example/live-update-feature/
